/*******************************************************************************
* Licensed Materials - Property of HCL
* (c) Copyright HCL Technologies Ltd. 2020, 2023. All Rights Reserved.

* Note to U.S. Government Users Restricted Rights:
* Use, duplication or disclosure restricted by GSA ADP Schedule
**************************************************************/

/**
 * Component that creates a KPI Info List
 * required params:
 *  - onClose
 *  - label
 *
 */

import React from "react";
import {
    Button,
    Text,
    TableAdvanced,
    Spinner,
    BackgroundPanel,
} from "vr-react-components";
import { useSelector } from "react-redux";
import { getFormattedTime } from "../../utils/TimeUtils";
import KPIWidgetFrontPanel from "./KPIWidgetFrontPanel";
import { handleSetKeyboard } from "../../handlers/PrivateHandlers";
import { handleGoToJobRoom } from "../../handlers/JobRoomHandlers";
import { handleGoToWorkstationRoom } from "../../handlers/WorkstationRoomHandlers";

function KPIWidgetInfo({
    width,
    height,
    onClose,
    label,
    fontColor,
    panel,
    ...otherAttributes
}) {
    const info = useSelector((state) => state.homeroom.kpi.info[label]);
    const kpiValue = useSelector((state) => state.homeroom.kpi.list[label]);
    const canModify = useSelector((state) => state.private.device.canModify);

    let infoOptions = {
        width: panel ? width * 0.8 : width * 0.9,
        height: height * 0.6,
        position: `${width / 2} ${-height * (panel ? 0.3 : 0.1)} ${width * 0.001}`,
        textSizeDecimal:
            width > (!panel ? height * 0.6 : height * 0.8)
                ? 65 / width
                : 65 / (!panel ? height * 0.6 : height * 0.8),
    };

    const getTableData = () => {
        switch (label) {
            case "Jobs In Late": {
                return {
                    data: [
                        ["Name", "Workstation", "Workflow", "Scheduled Time"],
                        ...info.map((x) => [
                            x.name,
                            x.workstation.name,
                            x.workflow,
                            getFormattedTime(x.scheduledTime),
                        ]),
                    ],
                    onRowClick: (position) => {
                        handleGoToJobRoom(info[position])
                    },
                    ...infoOptions,
                };
            }
            case "Jobs In Error": {
                return {
                    data: [
                        ["Name", "Workstation", "Workflow", "Scheduled Time"],
                        ...info.map((x) => [
                            x.name,
                            x.workstation.name,
                            x.workflow,
                            getFormattedTime(x.scheduledTime),
                        ]),
                    ],
                    onRowClick: (position) => {
                        handleGoToJobRoom(info[position])
                    },
                    ...infoOptions,
                };
            }
            case "Available Workstations": {
                return {
                    data: [["Name", "Limit"], ...info.map((x) => [x.name, x.limit])],
                    ...infoOptions,
                    onRowClick: (position) => {
                        handleGoToWorkstationRoom(info[position])
                    },
                };
            }
            case "Unavailable Workstations": {
                return {
                    data: [["Name", "Limit"], ...info.map((x) => [x.name, x.limit])],
                    ...infoOptions,
                    onRowClick: (position) => {
                        handleGoToWorkstationRoom(info[position])
                    },
                };
            }
            case "Pending Prompts": {
                return {
                    data: [
                        ["Name", "Message", "Type", "Status"],
                        ...info.map((x) => [
                            x.name,
                            x.message,
                            x.type,
                            x.state
                        ]),
                    ],
                    ...infoOptions,
                };
            }
            case "Min Duration": {
                return {
                    data: [
                        ["Name", "Workstation", "Workflow", "Scheduled Time"],
                        ...info.map((x) => [
                            x.name,
                            x.workstation.name,
                            x.workflow,
                            getFormattedTime(x.scheduledTime),
                        ]),
                    ],
                    onRowClick: (position) => {
                        handleGoToJobRoom(info[position])
                    },
                    ...infoOptions,
                };
            }
            default:
                return null;
        }
    };

    return (
        <a-entity {...otherAttributes}>
            {kpiValue !== null && label ? (
                <a-entity position={`${-width / 2} ${height / 2 - height / 20} 0.005`}>
                    {!panel ? (
                        <Text
                            text={label}
                            position={`${width / 20} 0 0 `}
                            width={width}
                            align="left"
                            sizeDecimal={30 / width}
                            color={fontColor}
                            baseline="top"
                        />
                    ) : (
                        <>
                            <BackgroundPanel
                                width={width * 0.9}
                                backgroundColor="#262626"
                                height={height * 0.2}
                                position={`${width * 0.5} ${-height * 0.1} 0`}
                            >
                                <KPIWidgetFrontPanel
                                    width={width * 0.9}
                                    height={height * 0.2}
                                    maxWidth={width * 0.9}
                                    value={kpiValue}
                                    isAlert={false}
                                    canOpenSettings={false}
                                    tile
                                    label={label}
                                    position="0 0 0.1"
                                    hideSettings
                                />
                            </BackgroundPanel>
                            <BackgroundPanel
                                width={width * 0.9}
                                backgroundColor="#262626"
                                height={height * 0.7}
                                position={`${width * 0.5} ${-height * 0.575} 0`}
                            />
                        </>
                    )}

                    {info ? (
                        <TableAdvanced
                            search
                            keyboardHandler={handleSetKeyboard}
                            aframeClasses={canModify ? "collidable" : ""}
                            {...getTableData()}
                        />
                    ) : (
                        <Spinner
                            radius={0.1}
                            position={`${width / 2} ${-height / 2 + height / 20} 0`}
                        />
                    )}

                    {!panel && (
                        <Button
                            text="Close"
                            aframeClasses={canModify ? "collidable" : ""}
                            position={`${width - width / 10 - width / 8} ${-height + height / 5
                                } 0`}
                            textSizeDecimal={10 / (width / 4)}
                            width={width / 4}
                            height={height / 10}
                            onClickHandler={onClose}
                            type={Button.constants.primary}
                        />
                    )}
                </a-entity>
            ) : (
                <BackgroundPanel
                    width={width * 0.975}
                    height={height * 0.975}
                    backgroundColor="#262626"
                >
                    <Text
                        text={label ? "No data available" : "No selected KPI"}
                        width={width}
                        align="center"
                        sizeDecimal={30 / width}
                        color={fontColor}
                    />
                </BackgroundPanel>
            )}
        </a-entity>
    );
}

// requiring data
KPIWidgetInfo.propTypes = {};

export default KPIWidgetInfo;
